import React, { useState, useEffect } from "react";
import { FaPhone, FaLock, FaEye, FaEyeSlash, FaArrowLeft, FaShieldAlt } from "react-icons/fa";
import { BiLoaderAlt } from "react-icons/bi";
import { IoIosArrowDropright } from "react-icons/io";
import { MdSms } from "react-icons/md";
import { RotatingLines } from "react-loader-spinner";

const ForgotPassword = () => {
  const [currentStep, setCurrentStep] = useState(1); // 1: Phone, 2: OTP, 3: Reset
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  
  // Form data
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Timer for OTP resend
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Step 1: Send OTP to phone number
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!phoneNumber) {
      alert("Please enter your phone number");
      return;
    }
    
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock API call would be:
      // const response = await axios.post(`${BASE_URL}/authentication/forgot-password/`, {
      //   phone: phoneNumber
      // });
      
      setCurrentStep(2);
      setTimeLeft(300); // 5 minutes
      alert(`OTP sent to ${phoneNumber}`);
    } catch (error) {
      alert("Error sending OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      alert("Please enter the 6-digit OTP code");
      return;
    }
    
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock API call would be:
      // const response = await axios.post(`${BASE_URL}/authentication/verify-otp/`, {
      //   phone: phoneNumber,
      //   otp: otpCode
      // });
      
      setCurrentStep(3);
      alert("OTP verified successfully");
    } catch (error) {
      alert("Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      alert("Please fill in both password fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      alert("Password must be at least 8 characters long");
      return;
    }
    
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock API call would be:
      // const response = await axios.post(`${BASE_URL}/authentication/reset-password/`, {
      //   phone: phoneNumber,
      //   otp: otpCode,
      //   new_password: newPassword
      // });
      
      alert("Password reset successful! You can now login with your new password.");
      // Redirect to login page
      window.location.href = "/login";
    } catch (error) {
      alert("Error resetting password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setTimeLeft(300);
      alert("New OTP sent to your phone");
    } catch (error) {
      alert("Error sending OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      window.location.href = "/login";
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step) => (
        <div key={step} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
            step <= currentStep 
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
              : 'bg-gray-200 text-gray-500'
          }`}>
            {step}
          </div>
          {step < 3 && (
            <div className={`w-12 h-0.5 mx-2 ${
              step < currentStep ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-gray-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderPhoneStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaPhone className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Forgot Password?</h3>
        <p className="text-gray-600">Enter your phone number and we'll send you a verification code</p>
      </div>

      <div>
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            Phone Number
          </label>
          <div className="relative group">
            <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200 ${
              focusedField === 'phone' ? 'text-blue-500' : 'text-gray-400'
            }`}>
              <FaPhone className="h-5 w-5" />
            </div>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onFocus={() => setFocusedField('phone')}
              onBlur={() => setFocusedField(null)}
              placeholder="Enter your phone number"
              className={`w-full pl-12 pr-4 py-4 bg-gray-50/50 border-2 rounded-xl text-gray-800 placeholder-gray-500 transition-all duration-200 outline-none ${
                focusedField === 'phone'
                  ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-white'
              }`}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold flex items-center justify-center gap-3 shadow-xl mt-6">
            <RotatingLines
              visible={true}
              height="20"
              width="20"
              color="transparent"
              strokeColor="white"
              strokeWidth="3"
              animationDuration="0.75"
            />
            Sending OTP...
          </div>
        ) : (
          <button 
            onClick={handleSendOTP}
            className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 active:scale-95 transition-all duration-200 mt-6"
          >
            Send Verification Code
            <IoIosArrowDropright className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );

  const renderOTPStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <MdSms className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Phone</h3>
        <p className="text-gray-600">We sent a 6-digit code to</p>
        <p className="text-blue-600 font-semibold">{phoneNumber}</p>
      </div>

      <form onSubmit={handleVerifyOTP}>
        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            Verification Code
          </label>
          <div className="relative group">
            <input
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onFocus={() => setFocusedField('otp')}
              onBlur={() => setFocusedField(null)}
              placeholder="Enter 6-digit code"
              className={`w-full px-4 py-4 bg-gray-50/50 border-2 rounded-xl text-gray-800 placeholder-gray-500 transition-all duration-200 outline-none text-center text-2xl tracking-widest ${
                focusedField === 'otp'
                  ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-white'
              }`}
              maxLength="6"
              required
            />
          </div>
        </div>

        {timeLeft > 0 ? (
          <div className="text-center text-sm text-gray-500">
            Resend code in {formatTime(timeLeft)}
          </div>
        ) : (
          <div className="text-center">
            <button 
              type="button"
              onClick={handleResendOTP}
              disabled={isLoading}
              className="text-sm text-blue-600 hover:text-blue-500 font-medium transition-colors duration-200"
            >
              Didn't receive the code? Resend
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold flex items-center justify-center gap-3 shadow-xl mt-6">
            <RotatingLines
              visible={true}
              height="20"
              width="20"
              color="transparent"
              strokeColor="white"
              strokeWidth="3"
              animationDuration="0.75"
            />
            Verifying...
          </div>
        ) : (
          <button 
            type="submit"
            className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 active:scale-95 transition-all duration-200 mt-6"
          >
            Verify Code
            <IoIosArrowDropright className="w-5 h-5" />
          </button>
        )}
      </form>
    </div>
  );

  const renderResetStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaShieldAlt className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Set New Password</h3>
        <p className="text-gray-600">Choose a strong password for your account</p>
      </div>

      <form onSubmit={handleResetPassword}>
        <div className="space-y-6">
          {/* New Password */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              New Password
            </label>
            <div className="relative group">
              <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200 ${
                focusedField === 'newPassword' ? 'text-blue-500' : 'text-gray-400'
              }`}>
                <FaLock className="h-5 w-5" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setFocusedField('newPassword')}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter new password"
                className={`w-full pl-12 pr-12 py-4 bg-gray-50/50 border-2 rounded-xl text-gray-800 placeholder-gray-500 transition-all duration-200 outline-none ${
                  focusedField === 'newPassword'
                    ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-white'
                }`}
                required
                minLength="8"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              Confirm Password
            </label>
            <div className="relative group">
              <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200 ${
                focusedField === 'confirmPassword' ? 'text-blue-500' : 'text-gray-400'
              }`}>
                <FaLock className="h-5 w-5" />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                placeholder="Confirm new password"
                className={`w-full pl-12 pr-12 py-4 bg-gray-50/50 border-2 rounded-xl text-gray-800 placeholder-gray-500 transition-all duration-200 outline-none ${
                  focusedField === 'confirmPassword'
                    ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-white'
                }`}
                required
                minLength="8"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                {showConfirmPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600 font-semibold mb-1">Password must contain:</p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li className="flex items-center">
                <div className={`w-1.5 h-1.5 rounded-full mr-2 ${newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                At least 8 characters
              </li>
              <li className="flex items-center">
                <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/[A-Z]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                One uppercase letter
              </li>
              <li className="flex items-center">
                <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/[0-9]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                One number
              </li>
            </ul>
          </div>
        </div>

        {isLoading ? (
          <div className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold flex items-center justify-center gap-3 shadow-xl mt-6">
            <RotatingLines
              visible={true}
              height="20"
              width="20"
              color="transparent"
              strokeColor="white"
              strokeWidth="3"
              animationDuration="0.75"
            />
            Resetting Password...
          </div>
        ) : (
          <button 
            type="submit"
            className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 active:scale-95 transition-all duration-200 mt-6"
          >
            Reset Password
            <IoIosArrowDropright className="w-5 h-5" />
          </button>
        )}
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-indigo-500/10 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 opacity-50"></div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl mb-4 shadow-2xl">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">LifeChemist</h1>
            <p className="text-blue-200">Password Recovery</p>
          </div>

          {/* Main Card */}
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 relative">
            {/* Decorative elements */}
            <div className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full blur-2xl opacity-20"></div>
            <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-xl opacity-20"></div>

            <div className="relative z-10">
              {/* Back Button */}
              <button
                onClick={goBack}
                className="flex items-center text-gray-600 hover:text-gray-800 mb-6 transition-colors duration-200"
              >
                <FaArrowLeft className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">
                  {currentStep === 1 ? 'Back to Login' : 'Back'}
                </span>
              </button>

              {/* Step Indicator */}
              {renderStepIndicator()}

              {/* Step Content */}
              {currentStep === 1 && renderPhoneStep()}
              {currentStep === 2 && renderOTPStep()}
              {currentStep === 3 && renderResetStep()}

              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-xs text-gray-500">
                  Need help? Contact support
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  © 2025 LifeChemist. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;